import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/common/SectionTitle";
import { useLanguage } from "@/context/LanguageContext";
import { getSiteContent } from "@/data/siteContent";

export default function ServicesPage() {
  const { language, isArabic } = useLanguage();
  const content = getSiteContent(language);
  const serviceCatalogRoutes = {
    cleaning: "/services/cleaning-catalog",
    hospitality: "/services/hospitality-catalog",
  };

  return (
    <section className="bg-white py-16 md:py-24" data-testid="services-page">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <SectionTitle
          badge={isArabic ? "خدماتنا" : "Our Services"}
          title={isArabic ? "توريد شامل لمتطلبات التشغيل اليومية" : "Complete supply coverage for daily operations"}
          description={isArabic ? "حلولنا تشمل مواد النظافة والضيافة، الاحتياجات المكتبية، وأجهزة الحاسب والطابعات ومواردها التشغيلية." : "Our solutions include hygiene and hospitality items, office needs, and computers/printers with their operating supplies."}
          testIdPrefix="services-main"
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {content.services.map((service, index) => {
            const Icon = service.icon;
            const title = isArabic ? service.titleAr : service.titleEn;
            const description = isArabic ? service.descriptionAr : service.descriptionEn;
            const serviceRoute = serviceCatalogRoutes[service.key];
            const isCatalogService = Boolean(serviceRoute);

            return (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
              >
                {isCatalogService ? (
                  <Link to={serviceRoute} className="block cursor-pointer" data-testid={`service-${service.key}-card-link`}>
                    <Card
                      className="h-full overflow-hidden border-slate-200 shadow-sm ring-2 ring-secondary/30 transition-shadow duration-300 hover:shadow-xl"
                      data-testid={`service-card-${service.key}`}
                    >
                      <img
                        src={service.image}
                        alt={title}
                        className="h-44 w-full object-cover"
                        data-testid={`service-image-${service.key}`}
                      />
                      <CardContent className="space-y-3 p-5">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10 text-secondary" data-testid={`service-icon-wrap-${service.key}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900" data-testid={`service-title-${service.key}`}>
                          {title}
                        </h3>
                        <p className="text-sm text-slate-600" data-testid={`service-description-${service.key}`}>
                          {description}
                        </p>
                        <span
                          className="inline-flex h-10 items-center justify-center rounded-md bg-secondary px-4 text-sm font-semibold text-white"
                          data-testid={`service-${service.key}-open-catalog-button`}
                        >
                          {isArabic ? "عرض محتوى الملف" : "Open PDF Content"}
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                ) : (
                  <Card className="h-full overflow-hidden border-slate-200 shadow-sm transition-shadow duration-300 hover:shadow-xl" data-testid={`service-card-${service.key}`}>
                    <img
                      src={service.image}
                      alt={title}
                      className="h-44 w-full object-cover"
                      data-testid={`service-image-${service.key}`}
                    />
                    <CardContent className="space-y-3 p-5">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10 text-secondary" data-testid={`service-icon-wrap-${service.key}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900" data-testid={`service-title-${service.key}`}>
                        {title}
                      </h3>
                      <p className="text-sm text-slate-600" data-testid={`service-description-${service.key}`}>
                        {description}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
